<template>
  <div class="rounded-2xl border p-5 transition-all duration-300"
    :class="isDark 
      ? 'bg-zinc-900/20 border-zinc-900 hover:border-zinc-800/80' 
      : 'bg-zinc-50/40 border-zinc-100 hover:border-zinc-200/60'">
    
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div class="space-y-0.5">
        <h2 class="text-sm font-normal tracking-wide transition-colors duration-200"
          :class="isDark ? 'text-white' : 'text-zinc-900'">Horaires d'Ouverture</h2>
        <p class="text-[11px] font-light transition-colors duration-200"
          :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Planning hebdomadaire de l'établissement</p>
      </div>
      <router-link
        to="/settings"
        class="text-[10px] font-mono uppercase tracking-wider transition-colors duration-200"
        :class="isDark ? 'text-[#D5CCBC] hover:text-white' : 'text-zinc-800 hover:text-zinc-500'"
      >
        Modifier →
      </router-link>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <svg class="animate-spin h-6 w-6" :class="isDark ? 'text-zinc-600' : 'text-zinc-300'" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>

    <!-- Liste des jours -->
    <div v-else class="space-y-1.5">
      <div
        v-for="(jour, key) in joursOrdre"
        :key="key"
        class="flex items-center justify-between p-3 rounded-xl border transition-all duration-200"
        :class="[
          isToday(key)
            ? (isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200/80 shadow-sm')
            : (isDark ? 'bg-zinc-950/40 border-zinc-900/60' : 'bg-transparent border-transparent')
        ]"
      >
        <div class="flex items-center gap-3">
          <div
            class="w-1.5 h-1.5 rounded-full transition-colors duration-200"
            :class="[
              isOpen(horaires[key])
                ? (isDark ? 'bg-emerald-500' : 'bg-emerald-600')
                : (isDark ? 'bg-zinc-800' : 'bg-zinc-200')
            ]"
          ></div>
          <span
            class="text-xs font-mono uppercase tracking-wider transition-colors duration-200"
            :class="[
              isToday(key)
                ? (isDark ? 'text-white font-medium' : 'text-zinc-900 font-semibold')
                : (isDark ? 'text-zinc-400' : 'text-zinc-500')
            ]"
          >
            {{ jour }}
          </span>
        </div>

        <div class="flex items-center gap-2">
          <template v-if="horaires[key] && horaires[key].length > 0">
            <div
              v-for="(slot, idx) in horaires[key]"
              :key="idx"
              class="flex items-center gap-1"
            >
              <span class="text-[10px] font-mono transition-colors duration-200"
                :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
                {{ slot.debut }}
              </span>
              <span class="text-[10px] transition-colors duration-200" :class="isDark ? 'text-zinc-700' : 'text-zinc-300'">-</span>
              <span class="text-[10px] font-mono transition-colors duration-200"
                :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
                {{ slot.fin }}
              </span>
              <span v-if="idx < horaires[key].length - 1" class="mx-1 transition-colors duration-200" :class="isDark ? 'text-zinc-800' : 'text-zinc-200'">|</span>
            </div>
          </template>
          <span v-else class="text-[10px] font-mono uppercase tracking-wider transition-colors duration-200"
            :class="isDark ? 'text-zinc-700' : 'text-zinc-300'">
            Fermé
          </span>
        </div>
      </div>
    </div>

    <!-- Pied de carte: Statut en temps réel -->
    <div class="mt-4 pt-4 border-t flex items-center justify-between transition-colors duration-200"
      :class="isDark ? 'border-zinc-900/60' : 'border-zinc-100'">
      <span class="text-[10px] font-mono uppercase tracking-widest"
        :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
        Statut actuel
      </span>
      <div class="flex items-center gap-2">
        <div
          class="w-1.5 h-1.5 rounded-full animate-pulse"
          :class="[
            isCurrentlyOpen 
              ? (isDark ? 'bg-emerald-500' : 'bg-emerald-600') 
              : (isDark ? 'bg-zinc-700' : 'bg-zinc-400')
          ]"
        ></div>
        <span
          class="text-xs font-medium tracking-wide transition-colors duration-200"
          :class="[
            isCurrentlyOpen 
              ? (isDark ? 'text-emerald-400' : 'text-emerald-700') 
              : (isDark ? 'text-zinc-500' : 'text-zinc-500')
          ]"
        >
          {{ isCurrentlyOpen ? 'Ouvert' : 'Fermé' }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import api from '@/api'
import { useTheme } from '@/composables/useTheme'

const { isDark } = useTheme()
const loading = ref(true)

const horaires = ref({
  lundi: [],
  mardi: [],
  mercredi: [],
  jeudi: [],
  vendredi: [],
  samedi: [],
  dimanche: []
})

const joursOrdre = {
  lundi: 'Lun',
  mardi: 'Mar',
  mercredi: 'Mer',
  jeudi: 'Jeu',
  vendredi: 'Ven',
  samedi: 'Sam',
  dimanche: 'Dim'
}

const joursSemaine = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi']

const loadHoraires = async () => {
  try {
    loading.value = true
    const token = localStorage.getItem('token')
    if (!token) return

    const response = await api.auth.getFirmInfo()
    const firmData = response.data.data || response.data

    if (firmData.horaires) {
      horaires.value = firmData.horaires
    }
  } catch (error) {
    console.error('Erreur lors du chargement des horaires:', error)
  } finally {
    loading.value = false
  }
}

const isToday = (jour) => {
  const today = new Date().getDay()
  const jourIndex = joursSemaine.indexOf(jour)
  return today === jourIndex
}

const isOpen = (slots) => {
  return slots && slots.length > 0
}

const isCurrentlyOpen = computed(() => {
  const now = new Date()
  const currentDay = joursSemaine[now.getDay()]
  const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`

  const daySlots = horaires.value[currentDay]
  if (!daySlots || daySlots.length === 0) return false

  return daySlots.some(slot => currentTime >= slot.debut && currentTime <= slot.fin)
})

onMounted(() => {
  loadHoraires()
})
</script>

<style scoped>
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>